import { getMobileBridge } from '../platform/mobile-bridge.js';

export type InstallCapabilitySnapshot = {
  hasPrompt: boolean;
  isInstalled: boolean;
  source: 'unsupported' | 'electron' | 'mobile' | 'installed' | 'pending' | 'browser';
};

export type InstallCapabilityListener = (snap: InstallCapabilitySnapshot) => void;

export interface InstallCapabilityController {
  snapshot(): InstallCapabilitySnapshot;
  subscribe(listener: InstallCapabilityListener): () => void;
  requestInstall(): Promise<'accepted' | 'dismissed' | 'unavailable' | 'failed'>;
  dispose(): void;
}

type BeforeInstallPromptEvent = Event & {
  readonly platforms: string[];
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed'; platform: string } | void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string } | void>;
};

type InstallState = InstallCapabilitySnapshot['source'];

interface Internal {
  setPending(event: BeforeInstallPromptEvent): void;
  setInstalled(): void;
  emit(): void;
  isDisposed(): boolean;
}

function snapshotFor(state: InstallState): InstallCapabilitySnapshot {
  return {
    hasPrompt: state === 'pending',
    isInstalled: state === 'installed' || state === 'mobile',
    source: state,
  };
}

function readState(): InstallState {
  if (typeof window === 'undefined') return 'unsupported';
  if (getMobileBridge()) return 'mobile';
  if (window.asisDesktop) return 'electron';
  if (bridgeState.installed || isDisplayStandalone()) return 'installed';
  if (bridgeState.pending) return 'pending';
  return 'browser';
}

export function isDisplayStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(standalone || iosStandalone);
}

const bridgeState: { installed: boolean; pending: BeforeInstallPromptEvent | null } = {
  installed: false,
  pending: null,
};

let bridgeActive = false;
let bridgeListeners: Array<{ name: string; fn: (event: Event) => void }> = [];

const active = new Set<Internal>();

function makeController(initialPending?: BeforeInstallPromptEvent | null): { controller: InstallCapabilityController; internal: Internal } {
  if (typeof window !== 'undefined') ensureBridge();
  if (initialPending) bridgeState.pending = initialPending;
  const ownListeners = new Set<InstallCapabilityListener>();
  let disposed = false;
  const emit = (): void => {
    if (disposed) return;
    const snap = snapshotFor(readState());
    for (const listener of ownListeners) listener(snap);
  };
  const internal: Internal = {
    setPending(event) {
      if (disposed) return;
      if (typeof event.preventDefault === 'function') event.preventDefault();
      bridgeState.pending = event;
      emit();
    },
    setInstalled() {
      if (disposed) return;
      bridgeState.installed = true;
      bridgeState.pending = null;
      emit();
    },
    emit,
    isDisposed: () => disposed,
  };
  const controller: InstallCapabilityController = {
    snapshot: () => snapshotFor(readState()),
    subscribe(listener) {
      if (disposed) return () => undefined;
      ownListeners.add(listener);
      listener(snapshotFor(readState()));
      return () => {
        ownListeners.delete(listener);
      };
    },
    async requestInstall() {
      if (disposed) return 'unavailable';
      if (readState() !== 'pending') return 'unavailable';
      const event = bridgeState.pending;
      if (!event) return 'unavailable';
      bridgeState.pending = null;
      for (const subscriber of active) subscriber.emit();
      try {
        const promptPromise = Promise.resolve(event.prompt());
        const choicePromise = Promise.resolve(event.userChoice);
        const [promptResult, choiceResult] = await Promise.all([promptPromise, choicePromise]);
        const vote = (choiceResult ?? promptResult) as
          | { outcome: 'accepted' | 'dismissed'; platform: string }
          | undefined;
        if (!vote || typeof vote.outcome !== 'string') return 'failed';
        if (vote.outcome === 'accepted') return 'accepted';
        return 'dismissed';
      } catch {
        return 'failed';
      }
    },
    dispose() {
      disposed = true;
      ownListeners.clear();
      active.delete(internal);
      if (singleton && singleton.internal === internal) singleton = null;
      if (active.size === 0) {
        bridgeState.pending = null;
      }
    },
  };
  active.add(internal);
  return { controller, internal };
}

function ensureBridge(): void {
  if (typeof window === 'undefined') return;
  if (getMobileBridge()) return;
  if (bridgeActive) return;
  bridgeActive = true;
  const onBip = (event: Event) => {
    const bip = event as BeforeInstallPromptEvent;
    if (typeof bip.preventDefault === 'function') bip.preventDefault();
    bridgeState.pending = bip;
    for (const internal of active) internal.emit();
  };
  const onInstalled = () => {
    bridgeState.installed = true;
    bridgeState.pending = null;
    for (const internal of active) internal.emit();
  };
  const onPageShow = () => {
    for (const internal of active) internal.emit();
  };
  window.addEventListener('beforeinstallprompt', onBip);
  window.addEventListener('appinstalled', onInstalled);
  window.addEventListener('pageshow', onPageShow);
  bridgeListeners = [
    { name: 'beforeinstallprompt', fn: onBip as (e: Event) => void },
    { name: 'appinstalled', fn: onInstalled as (e: Event) => void },
    { name: 'pageshow', fn: onPageShow as (e: Event) => void },
  ];
}

export function createInstallControllerForTests(): InstallCapabilityController {
  ensureBridge();
  return makeController().controller;
}

export function resetInstallStateForTests(): void {
  active.clear();
  bridgeState.installed = false;
  bridgeState.pending = null;
  if (typeof window !== 'undefined') {
    for (const { name, fn } of bridgeListeners) window.removeEventListener(name, fn);
    bridgeListeners = [];
    bridgeActive = false;
  }
  singleton = null;
}

let singleton: { internal: Internal; controller: InstallCapabilityController } | null = null;

export function getInstallController(): InstallCapabilityController {
  if (typeof window === 'undefined') return makeController().controller;
  ensureBridge();
  if (singleton && !singleton.internal.isDisposed()) return singleton.controller;
  const built = makeController();
  singleton = built;
  return built.controller;
}

export async function registerPwa(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (getMobileBridge()) return;
  ensureBridge();
  if (window.asisDesktop) return;
  if (location.protocol !== 'https:' || !('serviceWorker' in navigator)) return;
  try {
    await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
  } catch {
    /* Installation is optional; the online application still works. */
  }
}
